<script setup lang="ts">
// 守狸云收银台：开通会员 / 续费 / ¥1 应急解锁卡。瘦 BFF + 纯轮询。
// 微信 Native 下单拿 codeUrl 渲二维码；轮询订单到 paid 即成功（权益履约异步、稍晚于 paid）。
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import * as api from '../api'
import QrCode from './QrCode.vue'

const props = defineProps<{ mode: 'membership' | 'unlock' }>()
const emit = defineEmits<{ (e: 'paid'): void; (e: 'close'): void }>()

// 模态焦点管理：打开时把焦点移入对话框（关闭按钮），ESC 关闭，
// 焦点离开对话框时拉回（轻量焦点收束，避免 Tab 跑到背景内容）。
const dialogEl = ref<HTMLElement | null>(null)
const closeBtn = ref<HTMLElement | null>(null)
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close')
}
function onFocusIn(e: FocusEvent) {
  const root = dialogEl.value
  if (root && e.target instanceof Node && !root.contains(e.target)) {
    closeBtn.value?.focus()
  }
}

type Phase = 'pick' | 'creating' | 'pay' | 'paid' | 'error'
const phase = ref<Phase>('pick')
const skus = ref<api.SkuView[]>([])
const order = ref<api.OrderView | null>(null)
const error = ref('')
const loading = ref(false)

const POLL_MS = 2500
const TIMEOUT_MS = 5 * 60 * 1000
let pollTimer: number | null = null
let deadline = 0

const title = computed(() => (props.mode === 'unlock' ? '应急解锁卡' : '开通守狸云会员'))
// 注：短信重置（smsResetStart）目前是无 UI 的后端预留，Web 端尚无可用入口，
// 故收银台文案不再承诺"短信重置"，避免买后找不到功能（诚实降级，见 needs_user）。
const subtitle = computed(() =>
  props.mode === 'unlock'
    ? '¥1 解锁 24 小时全功能：应急查看不限次。'
    : '跨设备同步、设备丢失可恢复、陌生设备应急查看。',
)

function yuan(cents: number): string {
  return (cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: cents % 100 ? 2 : 0 })
}
function periodLabel(days?: number): string {
  if (days === 365) return '/ 年'
  if (days === 30) return '/ 月'
  if (days === 1) return '/ 24 小时'
  return days ? `/ ${days} 天` : ''
}

const membershipSkus = computed(() =>
  skus.value
    .filter((s) => s.kind === 'subscription' && s.code !== api.UNLOCK_CARD_SKU)
    .sort((a, b) => a.sortOrder - b.sortOrder),
)

async function loadSkus() {
  if (props.mode !== 'membership') return
  loading.value = true
  try {
    skus.value = await api.listSkus()
  } catch (e) {
    fail(e)
  } finally {
    loading.value = false
  }
}

async function buy(skuCode: string) {
  phase.value = 'creating'
  error.value = ''
  try {
    let created: api.OrderView
    if (props.mode === 'unlock') {
      const r = await api.purchaseUnlockCard()
      if (r.expiresAt && !r.codeUrl && !r.sandboxPayUrl) {
        // dev 直接核销（无 payment）：已生效。
        phase.value = 'paid'
        emit('paid')
        return
      }
      created = {
        id: r.orderId || '',
        skuCode: api.UNLOCK_CARD_SKU,
        skuKind: 'subscription',
        amountCents: (r.priceCny || 1) * 100,
        currency: 'CNY',
        channel: 'wechatpay-native',
        status: 'pending',
        codeUrl: r.codeUrl,
        sandboxPayUrl: r.sandboxPayUrl,
      }
    } else {
      created = await api.createOrder(skuCode, api.newIdempotencyKey())
    }
    order.value = created
    if (created.status === 'paid') {
      onPaid()
      return
    }
    phase.value = 'pay'
    startPoll()
  } catch (e) {
    fail(e)
  }
}

function startPoll() {
  stopPoll()
  deadline = Date.now() + TIMEOUT_MS
  pollTimer = window.setInterval(tick, POLL_MS)
}
function stopPoll() {
  if (pollTimer) {
    window.clearInterval(pollTimer)
    pollTimer = null
  }
}
async function tick() {
  if (!order.value?.id) return
  if (Date.now() > deadline) {
    stopPoll()
    error.value = '二维码已超时，请重新发起。'
    phase.value = 'error'
    return
  }
  try {
    const fresh = await api.getOrder(order.value.id)
    order.value = fresh
    if (fresh.status === 'paid') {
      onPaid()
    } else if (['failed', 'canceled', 'refunded'].includes(fresh.status)) {
      stopPoll()
      error.value = '支付未完成，请重试。'
      phase.value = 'error'
    }
  } catch {
    /* 轮询瞬时失败忽略，下次再试 */
  }
}
function onPaid() {
  stopPoll()
  phase.value = 'paid'
  emit('paid') // 父组件负责重查 membership（含 4s 补刷）
}

function fail(e: unknown) {
  stopPoll()
  const status = (e as { status?: number }).status
  error.value =
    status === 503 || status === 502
      ? '结算服务暂不可用，请稍后再试。'
      : String((e as Error)?.message || '下单失败，请重试。')
  phase.value = 'error'
}

function reset() {
  stopPoll()
  order.value = null
  error.value = ''
  phase.value = 'pick'
  if (props.mode === 'unlock') buy(api.UNLOCK_CARD_SKU)
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('focusin', onFocusIn)
  nextTick(() => closeBtn.value?.focus())
  if (props.mode === 'unlock') buy(api.UNLOCK_CARD_SKU)
  else loadSkus()
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('focusin', onFocusIn)
  stopPoll()
})
</script>

<template>
  <div class="kt-cashier-mask" @click.self="emit('close')">
    <div ref="dialogEl" class="kt-cashier" role="dialog" aria-modal="true" aria-labelledby="kt-cashier-title">
      <button ref="closeBtn" class="kt-cashier__x" aria-label="关闭" @click="emit('close')">×</button>
      <h2 id="kt-cashier-title" class="kt-cashier__title">{{ title }}</h2>
      <p class="kt-cashier__sub">{{ subtitle }}</p>

      <!-- 选档（会员）-->
      <div v-if="phase === 'pick' && mode === 'membership'" class="kt-sku-grid">
        <p v-if="loading" class="kt-hint">载入套餐…</p>
        <button v-for="s in membershipSkus" :key="s.code" class="kt-sku" @click="buy(s.code)">
          <span class="kt-sku__name">{{ s.name }}</span>
          <span class="kt-sku__price"><b>¥{{ yuan(s.priceCents) }}</b>{{ periodLabel(s.periodDays) }}</span>
        </button>
        <p v-if="!loading && !membershipSkus.length" class="kt-hint">暂无可购买的套餐。</p>
      </div>

      <p v-else-if="phase === 'creating'" class="kt-hint kt-center">正在创建订单…</p>

      <!-- 扫码支付 -->
      <div v-else-if="phase === 'pay'" class="kt-pay">
        <QrCode v-if="order?.codeUrl" :value="order.codeUrl" :size="208" />
        <p v-if="order?.codeUrl" class="kt-pay__tip">请用微信扫码支付 <b>¥{{ yuan(order!.amountCents) }}</b></p>
        <a
          v-else-if="order?.sandboxPayUrl"
          class="kt-btn kt-btn--primary"
          :href="order.sandboxPayUrl"
          target="_blank"
          rel="noopener"
        >测试支付（沙箱）</a>
        <p v-else class="kt-hint">等待支付渠道…</p>
        <p class="kt-pay__poll"><span class="kt-dot" /> 等待支付结果…</p>
      </div>

      <!-- 成功 -->
      <div v-else-if="phase === 'paid'" class="kt-paid">
        <div class="kt-paid__mark">✓</div>
        <p class="kt-paid__title">支付成功</p>
        <p class="kt-hint">权益正在生效，稍候即可使用。</p>
        <button class="kt-btn kt-btn--primary" @click="emit('close')">完成</button>
      </div>

      <!-- 失败 -->
      <div v-else-if="phase === 'error'" class="kt-paid">
        <p class="kt-warn">{{ error }}</p>
        <button class="kt-btn kt-btn--primary" @click="reset">重试</button>
        <button class="kt-btn kt-btn--ghost" @click="emit('close')">取消</button>
      </div>
    </div>
  </div>
</template>
